<template>
	<div class="form-container">
		<!-- Reward -->
		<CRow>
			<CCol md="12">
				<div class="d-flex mb-3 justify-content-between">
					<div class="mb-0">
						<h6 data-test-id="title">
							Reward
						</h6>
						<div
							class="typo-caption color-black-45"
							data-test-id="description"
						>
							Create Reward from Main Product + Bundle Product
						</div>
					</div>
					<div class="btn-add-wrapper">
						<CDropdown
							add-menu-classes="import-dropdown-menu"
							class="btn-dropdown text-right"
							color="secondary"
						>
							<CDropdownItem
								v-for="({ value, name }, index) in DAPP_REWARD_TYPE_OPTION"
								:key="index"
								@click="selectRewardType(value)"
							>
								{{ name }}
							</CDropdownItem>
							<template #toggler-content>
								<span>Create Reward</span>
							</template>
						</CDropdown>
					</div>
				</div>
			</CCol>
		</CRow>

		<!-- Empty Reward -->
		<BaseNoItemBanner
			v-if="!displayData || !displayData.rewards || !displayData.rewards.length"
			text="There is no reward."
		/>

		<!-- Start ======== -->
		<div v-else>
			<draggable
				:value="displayData.rewards"
				ghost-class="is-placeholder-item"
				:disabled="!isDraggable"
				class="d-flex flex-column reward-container-list"
				@input="handleUpdateOrder"
			>
				<CRow
					v-for="reward in displayData.rewards"
					:key="reward.uid"
				>
					<CCol md="12">
						<PromotionDappStepRewardForm
							:reward="reward"
							:uid="reward.uid"
							:reserve-skus="reserveSkus"
							@update:reward="handleSaveRewardForm"
							@onChangeStatus="handleChangeStatus"
							@onCancel="handleCancel"
							@onRemove="handleRemoveRewardModal"
						/>
					</CCol>
				</CRow>
			</draggable>
		</div>

		<!-- Modal Section -->
		<BaseModalConfirmDelete
			ref="modal-remove"
			:handle-remove="handleRemoveReward"
			title="Remove this reward"
			description="Are you sure you want to delete this reward?"
		/>
	</div>
</template>

<script>
import draggable from 'vuedraggable';
import { v4 as uuidv4 } from 'uuid';

import PromotionDappStepRewardForm from '@/components/PromotionDappStepRewardForm.vue';

import {
	PROMOTION_TYPES,
	CUSTOMER_REWARD_TYPE_OPTIONS,
	DAPP_REWARD_TYPE_OPTION,
	DAPP_REWARD_TYPE_LABELS,
	DAPP_REWARD_STATUS,
} from '../enums/promotions';

export default {
	name: 'PromotionDappStepReward',

	components: {
		draggable,
		PromotionDappStepRewardForm,
	},

	props: {
		defaultData: {
			type: Object,
			default: () => {},
		},
		isEditMode: {
			type: Boolean,
			default: false,
		},
		promotionType: {
			type: String,
			default: PROMOTION_TYPES.GENERAL,
		},
	},

	data() {
		const defaultReward = {
			order: 0,
			status: DAPP_REWARD_STATUS.DEFAULT, // create, edit, ''
			type: '', // DAPP_REWARD_TYPE_OPTION
			rewardItem: [],
			isStackable: false,
		};

		const defaultRewards = this.defaultData?.rewards || [];

		const formData = {
			rewards: defaultRewards.map((data) => {
				return {
					uid: uuidv4(), // For identify reward when delete
					status: DAPP_REWARD_STATUS.DEFAULT,
					...data,
				};
			}),
		};

		const displayData = JSON.parse(JSON.stringify(formData));
		return {
			DAPP_REWARD_TYPE_OPTION,
			CUSTOMER_REWARD_TYPE_OPTIONS,

			// variables
			defaultReward,
			displayData,
			slelectedDeleteUid: '',
			formData,
		};
	},

	computed: {
		reserveSkus() {
			if (!this.displayData || !this.displayData.rewards) {
				return [];
			}

			return this.displayData.rewards.flatMap((e) => e.rewardItem
				.map((i) => i.sku)
				.filter((sku) => sku != null));
		},
		isDraggable() {
			return !this.displayData.rewards
				.some((reward) => reward.status !== DAPP_REWARD_STATUS.DEFAULT);
		},
	},

	watch: {
		formData: {
			deep: true,
			handler(data) {
				this.$emit(
					'update:is-valid',
					{
						valid: data.rewards.length,
						data,
					},
				);
			},
		},
	},

	methods: {
		selectRewardType(type) {
			const newReward = JSON.parse(JSON.stringify(this.defaultReward));
			newReward.uid = uuidv4(); // For identify reward when delete
			newReward.status = DAPP_REWARD_STATUS.CREATE;
			newReward.type = {
				value: type,
				name: DAPP_REWARD_TYPE_LABELS[type],
			};
			this.displayData.rewards.push(newReward);
		},
		handleRemoveRewardModal(uid) {
			this.slelectedDeleteUid = uid;
			this.$refs['modal-remove'].open();
		},
		handleRemoveReward() {
			const currentIndex = this.displayData.rewards.findIndex((reward) => reward.uid === this.slelectedDeleteUid);
			if (!this.displayData.rewards[currentIndex]) {
				return;
			}

			this.displayData.rewards.splice(currentIndex, 1);
			this.formData.rewards = this.displayData.rewards.length
				? JSON.parse(JSON.stringify(this.displayData.rewards))
				: [];
		},
		handleChangeStatus(form) {
			const currentIndex = this.displayData.rewards.findIndex((reward) => reward.uid === form.uid);
			const newReward = JSON.parse(JSON.stringify(form));
			if (currentIndex < 0) {
				this.displayData.rewards.push(newReward);
			} else {
				this.displayData.rewards.splice(currentIndex, 1, newReward);
			}
		},
		handleCancel(form) {
			const currentIndex = this.formData.rewards.findIndex((reward) => reward.uid === form.uid);
			this.displayData.rewards = this.formData.rewards.length
				? JSON.parse(JSON.stringify(this.formData.rewards))
				: [];
			if (currentIndex >= 0) {
				this.displayData.rewards[currentIndex].status = DAPP_REWARD_STATUS.DEFAULT;
			}
		},
		handleSaveRewardForm(form) {
			const currentIndex = this.displayData.rewards.findIndex((reward) => reward.uid === form.uid);
			const newReward = JSON.parse(JSON.stringify(form));
			newReward.status = DAPP_REWARD_STATUS.DEFAULT;
			if (currentIndex < 0) {
				this.formData.rewards.push(newReward);
			} else {
				this.formData.rewards.splice(currentIndex, 1, newReward);
			}

			this.displayData = JSON.parse(JSON.stringify(this.formData));
		},
		handleUpdateOrder(sortedList) {
			this.displayData.rewards = sortedList;
			this.formData.rewards = sortedList.length
				? JSON.parse(JSON.stringify(sortedList))
				: [];
		},
	},
};
</script>

<style lang="scss" scoped>
	.form-container {
		margin-top: rem(80);
	}

	.reward-container-list {
		gap: 12px;
	}
</style>
